import { useState } from 'react';

import { describeApiError } from '../../lib/api-client';
import { inputStyle, mono, primaryButton, secondaryButton } from '../proxy-vault/ui';

import type { CreateUserSshKeyBody, UserSshKeyResponse } from '../../lib/api-client';

const KEY_TYPE_LABEL: Record<string, string> = {
  'ssh-ed25519': 'ed25519',
  'ecdsa-sha2-nistp256': 'ecdsa-p256',
  'ecdsa-sha2-nistp384': 'ecdsa-p384',
  'ecdsa-sha2-nistp521': 'ecdsa-p521',
  'ssh-rsa': 'rsa',
};

/** Wire key type → the short label operators recognise. */
export function keyTypeLabel(keyType: string): string {
  return KEY_TYPE_LABEL[keyType] ?? keyType;
}

interface Props {
  /** Whose key this is — the email, so an admin pasting for someone else sees the target. */
  userLabel: string;
  onCancel: () => void;
  onAdd: (body: CreateUserSshKeyBody) => Promise<UserSshKeyResponse>;
}

const fieldLabel: React.CSSProperties = { fontSize: 12, color: 'var(--text-soft)', display: 'block' };

/**
 * "Add SSH public key": label + the one-line OpenSSH public key. Validation is server-side
 * (ed25519 · ecdsa p256/p384/p521 · rsa ≥ 3072; duplicates and pasted private keys are 422s
 * whose RFC 9457 detail shows inline). On success the modal shows the computed fingerprint
 * so the operator can compare it with `ssh-keygen -lf -E sha256`.
 */
export function AddSshKeyModal({ userLabel, onCancel, onAdd }: Props) {
  const [name, setName] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<UserSshKeyResponse | null>(null);

  const canSubmit = name.trim().length > 0 && publicKey.trim().length > 0 && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      setAdded(await onAdd({ name: name.trim(), public_key: publicKey.trim() }));
    } catch (err) {
      setError(describeApiError(err, 'The key was not accepted'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="sshkey-modal"
      onClick={(e) => e.target === e.currentTarget && onCancel()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        role="dialog"
        aria-labelledby="sshkey-modal-title"
        style={{
          width: 460,
          maxWidth: '92vw',
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div>
          <div id="sshkey-modal-title" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            Add SSH public key
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-soft)', marginTop: 2 }}>for {userLabel}</div>
        </div>

        {added ? (
          <>
            <div style={{ fontSize: 12, color: 'var(--text)' }}>
              Key accepted — <strong>{added.name}</strong> · {keyTypeLabel(added.key_type)}.
            </div>
            <div>
              <span style={fieldLabel}>Fingerprint</span>
              <div
                data-testid="sshkey-modal-fingerprint"
                style={{ fontFamily: mono, fontSize: 11, color: 'var(--text)', wordBreak: 'break-all', marginTop: 4 }}
              >
                {added.fingerprint}
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', lineHeight: 1.5 }}>
              Compare it with <span style={{ fontFamily: mono }}>ssh-keygen -lf -E sha256 &lt;key&gt;.pub</span> on the
              machine that holds the private key. Uploading a key never grants access on its own — the role must carry
              SFTP access.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="button" data-testid="sshkey-modal-done" style={primaryButton} onClick={onCancel}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <label style={fieldLabel}>
              Label
              <input
                data-testid="sshkey-modal-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="laptop"
                maxLength={80}
                autoFocus
                style={{ ...inputStyle, width: '100%', marginTop: 4, boxSizing: 'border-box' }}
              />
            </label>
            <label style={fieldLabel}>
              Public key (one line, OpenSSH format)
              <textarea
                data-testid="sshkey-modal-key"
                value={publicKey}
                onChange={(e) => setPublicKey(e.target.value)}
                placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAI… comment"
                rows={3}
                spellCheck={false}
                style={{
                  ...inputStyle,
                  width: '100%',
                  marginTop: 4,
                  boxSizing: 'border-box',
                  fontFamily: mono,
                  fontSize: 11,
                  resize: 'vertical',
                }}
              />
            </label>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', lineHeight: 1.5 }}>
              Accepted: ed25519 · ecdsa p256/p384/p521 · rsa ≥ 3072. We never generate keys for you — paste your own
              public key (<span style={{ fontFamily: mono }}>cat ~/.ssh/id_ed25519.pub</span>); the private half never
              leaves your machine.
            </div>
            {error && (
              <div data-testid="sshkey-modal-error" role="alert" style={{ fontSize: 12, color: 'var(--kind-failure)' }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" style={secondaryButton} onClick={onCancel} disabled={busy}>
                Cancel
              </button>
              <button
                type="button"
                data-testid="sshkey-modal-submit"
                style={{ ...primaryButton, opacity: canSubmit ? 1 : 0.5 }}
                onClick={() => void submit()}
                disabled={!canSubmit}
              >
                {busy ? 'Adding…' : 'Add key'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
